import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useForgotPassword, useResetPassword } from '@/api-services/hooks/useAuth';
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  ArrowLeft,
  CheckCircle,
  Shield,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(token ? 2 : 1);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [email, setEmail] = useState('');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
    requireEmailChange: false,
    enableTwoFactor: false,
    logOutOtherSessions: true,
    notifySecurityChange: true
  });

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: '',
    color: 'bg-red-500'
  });

  const forgotPasswordMutation = useForgotPassword({
    onSuccess: () => {
      setIsEmailSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send reset email. Please try again.');
    },
  });

  const resetPasswordMutation = useResetPassword({
    onSuccess: () => {
      toast.success('Password reset successfully! You can now login with your new password.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reset password. The link may have expired.');
    },
  });

  const totalSteps = 2;
  const progress = (currentStep / totalSteps) * 100;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    let feedback = '';
    let color = 'bg-red-500';

    if (password.length === 0) {
      return { score: 0, feedback: 'Enter a password', color: 'bg-gray-300' };
    }

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;

    // Determine strength
    if (score <= 2) {
      feedback = 'Weak password';
      color = 'bg-red-500';
    } else if (score <= 4) {
      feedback = 'Fair password';
      color = 'bg-yellow-500';
    } else if (score <= 5) {
      feedback = 'Good password';
      color = 'bg-blue-500';
    } else {
      feedback = 'Strong password';
      color = 'bg-green-500';
    }

    return { score, feedback, color };
  };

  const handlePasswordChange = (password) => {
    setFormData(prev => ({ ...prev, newPassword: password }));
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const handleSendResetEmail = async (e) => {
    e.preventDefault();

    if (!email || !validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await forgotPasswordMutation.mutate({ email });
    } finally {
      setIsLoading(false);
    }
  };

  const validatePasswordReset = () => {
    if (!formData.newPassword || !formData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return false;
    }

    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return false;
    }

    if (passwordStrength.score < 3) {
      toast.error('Please choose a stronger password');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!validatePasswordReset()) return;

    setIsLoading(true);
    try {
      await resetPasswordMutation.mutate({
        token: token || '',
        password: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const passwordRequirements = [
    { text: 'At least 8 characters', met: formData.newPassword.length >= 8 },
    { text: 'Contains uppercase letter', met: /[A-Z]/.test(formData.newPassword) },
    { text: 'Contains lowercase letter', met: /[a-z]/.test(formData.newPassword) },
    { text: 'Contains a number', met: /[0-9]/.test(formData.newPassword) },
    { text: 'Contains special character', met: /[^a-zA-Z0-9]/.test(formData.newPassword) }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Back to login */}
        <div className="mb-8">
          <Link to="/login" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to login
          </Link>
        </div>

        <Card className="border-border/50 shadow-elegant">
          <CardHeader className="space-y-4 pb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {token ? 'Reset Your Password' : 'Forgot Password'}
                </h1>
                <p className="text-muted-foreground">
                  {token
                    ? 'Create a new secure password for your account'
                    : 'Enter your email to receive a password reset link'
                  }
                </p>
              </div>
            </div>

            {!token && (
              <Progress value={progress} className="h-2" />
            )}
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Step 1: Request Reset Email */}
            {!token && currentStep === 1 && !isEmailSent && (
              <form onSubmit={handleSendResetEmail} className="space-y-6">
                <div className="space-y-4">
                  <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
                      <div>
                        <h3 className="font-medium text-warning">Security Notice</h3>
                        <p className="text-sm text-warning/80 mt-1">
                          We'll send a secure link to your email that expires in 60 minutes.
                          This link can only be used once.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your registered email"
                        className="pl-10"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-primary hover:opacity-90 h-12"
                  disabled={isLoading || forgotPasswordMutation.loading}
                >
                  {(isLoading || forgotPasswordMutation.loading) ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Sending Reset Link...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Send Reset Link
                    </>
                  )}
                </Button>

                <div className="text-center text-sm">
                  <span className="text-muted-foreground">Remember your password? </span>
                  <Link to="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
                    Sign in
                  </Link>
                </div>
              </form>
            )}

            {/* Email Sent Confirmation */}
            {!token && isEmailSent && (
              <div className="space-y-6">
                <div className="bg-success/10 border border-success/20 rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <CheckCircle className="w-8 h-8 text-success mt-1" />
                    <div>
                      <h3 className="text-lg font-medium text-success">Reset Link Sent!</h3>
                      <p className="text-success/80 mt-2">
                        We've sent a password reset link to <strong>{email}</strong>
                      </p>
                      <ul className="mt-4 space-y-2 text-sm text-success/80">
                        <li className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>Check your inbox and spam folder</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>Link expires in 60 minutes</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>For security, the link can only be used once</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-center text-sm text-muted-foreground">
                    Didn't receive the email?
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setIsEmailSent(false);
                      setCurrentStep(1);
                    }}
                    disabled={isLoading}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Resend Reset Link
                  </Button>
                  <Link to="/login">
                    <Button variant="ghost" className="w-full">
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Step 2: Reset Password */}
            {token && (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your new password"
                        className="pl-10 pr-10"
                        value={formData.newPassword}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {formData.newPassword && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Password Strength</span>
                          <span className={`text-xs font-medium ${
                            passwordStrength.score <= 2 ? 'text-red-500' :
                            passwordStrength.score <= 4 ? 'text-yellow-500' :
                            'text-green-500'
                          }`}>
                            {passwordStrength.feedback}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                            style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Password Requirements */}
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">Password must contain:</p>
                      <ul className="space-y-1">
                        {passwordRequirements.map((requirement, index) => (
                          <li key={index} className="flex items-center space-x-2 text-xs">
                            <CheckCircle className={`w-3 h-3 ${
                              requirement.met ? 'text-green-500' : 'text-gray-300'
                            }`} />
                            <span className={requirement.met ? 'text-green-500' : 'text-gray-500'}>
                              {requirement.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your new password"
                        className="pl-10 pr-10"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Security Options */}
                  <div className="space-y-3 pt-4 border-t">
                    <h3 className="text-sm font-medium text-foreground">Security Options</h3>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="logOutOthers"
                        checked={formData.logOutOtherSessions}
                        onCheckedChange={(checked) =>
                          setFormData(prev => ({
                            ...prev,
                            logOutOtherSessions: checked === true,
                          }))
                        }
                      />
                      <Label htmlFor="logOutOthers" className="text-sm leading-5">
                        Log out from all other devices (recommended)
                      </Label>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="notifyChange"
                        checked={formData.notifySecurityChange}
                        onCheckedChange={(checked) =>
                          setFormData(prev => ({
                            ...prev,
                            notifySecurityChange: checked === true,
                          }))
                        }
                      />
                      <Label htmlFor="notifyChange" className="text-sm leading-5">
                        Send security notification to my email
                      </Label>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-primary hover:opacity-90 h-12"
                  disabled={isLoading || resetPasswordMutation.loading}
                >
                  {(isLoading || resetPasswordMutation.loading) ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5 mr-2" />
                      Reset Password
                    </>
                  )}
                </Button>

                <div className="text-center text-sm">
                  <span className="text-muted-foreground">Remember your password? </span>
                  <Link to="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
                    Sign in
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
